import { z } from 'zod'
import type { ToolType } from '../toolData'

// Core time shift interfaces
export interface DeviceTimeShift {
  id?: string
  projectId: string
  deviceType: ToolType
  deviceIdentifier?: string  // Optional: specific tool serial number
  timeOffsetMs: number      // Offset in milliseconds (positive = advance, negative = delay)
  createdAt?: string
  updatedAt?: string
  userId: string
  organizationId: string
}

export interface TimeShiftConfig {
  projectId: string
  shifts: Map<string, DeviceTimeShift>  // Key: deviceType-deviceIdentifier
}

export interface TimeShiftState {
  isLoading: boolean
  config: TimeShiftConfig | null
  error: string | null
  hasUnsavedChanges: boolean
}

// UI-specific interfaces
export interface DeviceGroupInfo {
  deviceType: ToolType
  fileCount: number
  activeShift?: DeviceTimeShift
  hasShift: boolean
}

export interface TimeShiftPanelProps {
  projectId: string
  devices: DeviceGroupInfo[]
  timeShiftConfig: TimeShiftConfig | null
  onTimeShiftChange: (deviceType: ToolType, offsetMs: number, deviceIdentifier?: string) => void
  onResetAll: () => void
  onApplyToAllCharts: () => void
  isLoading?: boolean
  error?: string | null
}

// Database types for Supabase integration
export interface DbDeviceTimeShift {
  id: string
  project_id: string
  device_type: string  // Will be ToolType values
  device_identifier: string | null
  time_offset_ms: number
  created_at: string
  updated_at: string
  user_id: string
  organization_id: string
}

// API request/response types
export interface CreateTimeShiftRequest {
  projectId: string
  deviceType: ToolType
  deviceIdentifier?: string
  timeOffsetMs: number
}

export interface UpdateTimeShiftRequest {
  id: string
  timeOffsetMs: number
}

export interface TimeShiftResponse {
  data: DeviceTimeShift[]
  error?: string
}

// Zod validation schemas
export const ToolTypeSchema = z.enum(['MicroPulse', 'Cortex', 'Innova', 'Vertex'])

export const DeviceTimeShiftSchema = z.object({
  id: z.string().uuid().optional(),
  projectId: z.string().uuid(),
  deviceType: ToolTypeSchema,
  deviceIdentifier: z.string().optional(),
  timeOffsetMs: z.number().int().min(-86400000).max(86400000), // ±24 hours in ms
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  userId: z.string().min(1),
  organizationId: z.string().min(1)
})

export const TimeShiftConfigSchema = z.object({
  projectId: z.string().uuid(),
  shifts: z.map(z.string(), DeviceTimeShiftSchema)
})

export const CreateTimeShiftRequestSchema = z.object({
  projectId: z.string().uuid(),
  deviceType: ToolTypeSchema,
  deviceIdentifier: z.string().optional(),
  timeOffsetMs: z.number().int().min(-86400000).max(86400000)
})

export const UpdateTimeShiftRequestSchema = z.object({
  id: z.string().uuid(),
  timeOffsetMs: z.number().int().min(-86400000).max(86400000)
})

// Helper functions for data transformation
export function mapDbToTimeShift(db: DbDeviceTimeShift): DeviceTimeShift {
  return {
    id: db.id,
    projectId: db.project_id,
    deviceType: db.device_type as ToolType,
    deviceIdentifier: db.device_identifier || undefined,
    timeOffsetMs: db.time_offset_ms,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
    userId: db.user_id,
    organizationId: db.organization_id
  }
}

export function mapTimeShiftToDb(shift: DeviceTimeShift): Omit<DbDeviceTimeShift, 'id' | 'created_at' | 'updated_at'> {
  return {
    project_id: shift.projectId,
    device_type: shift.deviceType,
    device_identifier: shift.deviceIdentifier || null,
    time_offset_ms: shift.timeOffsetMs,
    user_id: shift.userId,
    organization_id: shift.organizationId
  }
}

// Utility functions
export function createDeviceKey(deviceType: ToolType, deviceIdentifier?: string): string {
  return deviceIdentifier ? `${deviceType}-${deviceIdentifier}` : deviceType
}

export function parseDeviceKey(key: string): { deviceType: ToolType; deviceIdentifier?: string } {
  const parts = key.split('-')
  const deviceType = parts[0] as ToolType
  const deviceIdentifier = parts.length > 1 ? parts.slice(1).join('-') : undefined
  return { deviceType, deviceIdentifier }
}

export function formatTimeOffset(offsetMs: number): string {
  const absMs = Math.abs(offsetMs)
  const sign = offsetMs >= 0 ? '+' : '-'
  
  if (absMs < 1000) {
    return `${sign}${absMs}ms`
  }
  
  const seconds = absMs / 1000
  if (seconds < 60) {
    return `${sign}${seconds.toFixed(1)}s`
  }
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (remainingSeconds === 0) {
    return `${sign}${minutes}m`
  }
  
  return `${sign}${minutes}m ${remainingSeconds.toFixed(1)}s`
}

// Default values
export const DEFAULT_TIME_SHIFT: Omit<DeviceTimeShift, 'id' | 'projectId' | 'userId' | 'organizationId'> = {
  deviceType: 'MicroPulse',
  timeOffsetMs: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
}

export const EMPTY_TIME_SHIFT_CONFIG: Omit<TimeShiftConfig, 'projectId'> = {
  shifts: new Map()
}

export const INITIAL_TIME_SHIFT_STATE: TimeShiftState = {
  isLoading: false,
  config: null,
  error: null,
  hasUnsavedChanges: false
} 